import React, { useEffect, useRef, useState } from 'react';
import { ExternalLink, Loader2, Search, X } from 'lucide-react';
import { api } from '../lib/api';
import type { FloretPlant, PlantType } from '../types';

// ── Tag display helpers ──────────────────────────────────────────────────────

const TRAIT_LABELS: Record<string, string> = {
  'easy-to-grow':        'Easy to grow',
  'heat-lover':          'Heat-loving',
  'edible':              'Edible',
  'foliage-filler':      'Foliage/filler',
  'fragrant':            'Fragrant',
  'dryable':             'Dryable',
  'self-seeder':         'Self-seeder',
  'heavy-producer':      'Heavy producer',
  'medium-producer':     'Medium producer',
  'light-producer':      'Light producer',
  'long-vase-life':      'Long vase life',
  'tender-perennial':    'Tender perennial',
  'cut-flower':          'Cut flower',
  'cut-and-come-again':  'Cut & come again',
  'pollinator-friendly': 'Pollinator-friendly',
  'deer-resistant':      'Deer-resistant',
  'drought-tolerant':    'Drought-tolerant',
  'shade-tolerant':      'Shade-tolerant',
  'annual':              'Annual',
  'perennial':           'Perennial',
  'biennial':            'Biennial',
  'bulb':                'Bulb',
};

// Tags that are structural (type classifiers, form descriptors, color prefixes) — skip from notes
const SKIP_TAG_PREFIXES = [
  'color-', 'form-', 'grow-', 'seeds-',
  // Species names that appear as tags redundantly
  'dahlia', 'zinnia', 'sunflower', 'anemone', 'peony', 'tulip', 'daffodil',
  'amaranth', 'lisianthus', 'snapdragon', 'ranunculus', 'hellebore',
];

function getDisplayTraits(tags: string[]): string[] {
  return tags
    .filter((t) => !SKIP_TAG_PREFIXES.some((p) => t.startsWith(p)))
    .map((t) => TRAIT_LABELS[t])
    .filter(Boolean)
    .slice(0, 5);
}

function getColors(tags: string[]): string[] {
  return tags
    .filter((t) => t.startsWith('color-'))
    .map((t) => t.replace('color-', '').replace(/-/g, ' '))
    .slice(0, 3);
}

// ── Plant-type mapper ────────────────────────────────────────────────────────

export function floretTypeToPlantType(floretType: string, tags: string[]): PlantType {
  const t = floretType.toLowerCase();
  const tagSet = new Set(tags.map((tag) => tag.toLowerCase()));

  if (t.includes('dahlia')) return 'perennial';
  if (t.includes('tulip') || t.includes('daffodil') || t.includes('narcissus')
      || t.includes('specialty bulb') || t === 'bulbs') return 'bulb';
  if (t.includes('bearded iris') || t.includes('iris')) return 'perennial';
  if (t.includes('peony')) return 'perennial';
  if (t.includes('lilac')) return 'shrub';
  if (t.includes('chrysanthemum')) return 'perennial';
  if (t.includes('shrub')) return 'shrub';
  if (t.includes('tree')) return 'tree';
  if (t.includes('herb')) return 'herb';
  if (t.includes('grass')) return 'grass';
  if (tagSet.has('perennial') || tagSet.has('tender-perennial')) return 'perennial';
  if (tagSet.has('biennial')) return 'annual';
  if (tagSet.has('bulb')) return 'bulb';
  if (tagSet.has('annual') || t.includes('seed')) return 'annual';
  return 'annual'; // Floret is primarily a cut-flower/seed catalog; safe default
}

// ── Build notes string from Floret data ──────────────────────────────────────

export function floretToNotes(plant: FloretPlant): string {
  const traits = getDisplayTraits(plant.tags);
  const colors = getColors(plant.tags);
  const parts: string[] = [];
  if (colors.length) parts.push(`Colors: ${colors.join(', ')}`);
  if (traits.length) parts.push(traits.join(' · '));
  parts.push(`Floret Library: ${plant.url}`);
  return parts.join('\n');
}

// ── Component ────────────────────────────────────────────────────────────────

export interface FloretSelection {
  title: string;
  plant_type: PlantType;
  notes: string;
}

interface FloretLookupProps {
  /** Called when the user selects a variety */
  onSelect: (data: FloretSelection) => void;
  /** Pre-seed the search box from the current common_name value */
  initialQuery?: string;
}

export function FloretLookup({ onSelect, initialQuery = '' }: FloretLookupProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState(initialQuery);
  const [results, setResults] = useState<FloretPlant[]>([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Focus input when panel opens
  useEffect(() => {
    if (open) {
      setQuery(initialQuery);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  // Debounced search
  useEffect(() => {
    if (!open) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (query.trim().length < 2) { setResults([]); setSearched(false); return; }

    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const { products } = await api.floretSearch(query.trim());
        setResults(products);
        setSearched(true);
      } catch {
        setResults([]);
        setSearched(true);
      } finally {
        setLoading(false);
      }
    }, 350);

    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [query, open]);

  const handleSelect = (plant: FloretPlant) => {
    onSelect({
      title: plant.title,
      plant_type: floretTypeToPlantType(plant.type, plant.tags),
      notes: floretToNotes(plant),
    });
    setOpen(false);
    setQuery('');
    setResults([]);
    setSearched(false);
  };

  return (
    <div>
      {/* Toggle button */}
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center gap-1.5 text-xs text-garden-600 dark:text-garden-400 hover:text-garden-700 dark:hover:text-garden-300 transition-colors mt-1"
      >
        <span>🌸</span>
        <span>{open ? 'Close Floret lookup' : 'Look up in Floret Library'}</span>
      </button>

      {/* Inline panel */}
      {open && (
        <div className="mt-2 border border-garden-200 dark:border-garden-800 rounded-xl bg-white dark:bg-gray-900 shadow-sm overflow-hidden">
          {/* Search bar */}
          <div className="flex items-center gap-2 px-3 py-2 border-b border-gray-100 dark:border-gray-800">
            {loading
              ? <Loader2 size={14} className="text-gray-400 shrink-0 animate-spin" />
              : <Search size={14} className="text-gray-400 shrink-0" />
            }
            <input
              ref={inputRef}
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search 1,500+ Floret varieties…"
              aria-label="Search Floret Library"
              className="flex-1 text-sm bg-transparent text-gray-900 dark:text-gray-100 placeholder:text-gray-400 dark:placeholder:text-gray-500 focus:outline-none"
            />
            {query && (
              <button
                type="button"
                onClick={() => { setQuery(''); setResults([]); setSearched(false); }}
                aria-label="Clear search"
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <X size={13} />
              </button>
            )}
          </div>

          {/* Results */}
          <div className="max-h-72 overflow-y-auto">
            {!searched && query.trim().length < 2 && (
              <p className="text-xs text-gray-400 dark:text-gray-500 text-center py-5 px-3">
                Type a flower name — dahlias, zinnias, peonies…
              </p>
            )}

            {searched && results.length === 0 && (
              <p className="text-xs text-gray-400 dark:text-gray-500 text-center py-5 px-3">
                No matches in the Floret Library
              </p>
            )}

            {results.map((plant) => {
              const traits = getDisplayTraits(plant.tags);
              const colors = getColors(plant.tags);
              return (
                <div
                  key={plant.id}
                  className="flex items-center gap-3 px-3 py-2.5 hover:bg-gray-50 dark:hover:bg-gray-800 border-b border-gray-50 dark:border-gray-800 last:border-0 transition-colors"
                >
                  {/* Thumbnail */}
                  <div className="w-12 h-12 rounded-lg overflow-hidden bg-gray-100 dark:bg-gray-700 shrink-0">
                    {plant.image ? (
                      <img
                        src={plant.image}
                        alt={plant.title}
                        className="w-full h-full object-cover"
                        loading="lazy"
                        onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }}
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-xl">🌸</div>
                    )}
                  </div>

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate leading-snug">
                      {plant.title}
                    </p>
                    <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 mt-0.5">
                      {plant.type && (
                        <span className="text-xs text-garden-600 dark:text-garden-400 font-medium">
                          {plant.type}
                        </span>
                      )}
                      {colors.length > 0 && (
                        <span className="text-xs text-gray-400 capitalize">
                          {colors.join(', ')}
                        </span>
                      )}
                      {traits.slice(0, 2).map((t) => (
                        <span key={t} className="text-xs text-gray-400">· {t}</span>
                      ))}
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-1.5 shrink-0">
                    <a
                      href={plant.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={`View ${plant.title} on Floret Library`}
                      className="p-1 text-gray-300 dark:text-gray-600 hover:text-gray-500 dark:hover:text-gray-300 transition-colors"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <ExternalLink size={13} />
                    </a>
                    <button
                      type="button"
                      onClick={() => handleSelect(plant)}
                      className="text-xs font-medium px-2.5 py-1 rounded-md bg-garden-50 dark:bg-garden-900/40 text-garden-700 dark:text-garden-300 hover:bg-garden-100 dark:hover:bg-garden-900 transition-colors"
                    >
                      Use
                    </button>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Footer credit */}
          {results.length > 0 && (
            <div className="px-3 py-1.5 bg-gray-50 dark:bg-gray-800/50 border-t border-gray-100 dark:border-gray-800">
              <p className="text-[10px] text-gray-400 dark:text-gray-500">
                Data from{' '}
                <a
                  href="https://library.floretflowers.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline hover:text-gray-600 dark:hover:text-gray-300"
                >
                  Floret Flower Library
                </a>{' '}
                — free reference database of 1,500+ cut flower varieties
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
